/* CYME CUSTOM START: homepage native header state sync */
(() => {
  const updateHeaderState = () => {
    document.documentElement.classList.toggle('cyme-home-chrome-solid', window.scrollY > 24);
  };

  let scheduled = false;

  const scheduleUpdate = () => {
    if (scheduled) {
      return;
    }

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      updateHeaderState();
    });
  };

  const init = () => {
    updateHeaderState();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    document.addEventListener('shopify:section:load', updateHeaderState);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
/* CYME CUSTOM END: homepage native header state sync */

/* CYME CUSTOM START: homepage product rail marquee (continuous auto-scroll).
   The rail glides at constant velocity (Aiayu reference: transform marquee with
   cloned slides). Built on the native scroll position so Prestige's arrows and
   drag keep working: leading cells are cloned to the end for a seamless wrap,
   snap is off on marquee rails (free-flow track), hover/focus pause the glide,
   and any real interaction (drag, wheel, arrows) hands control back to the
   native carousel, resuming the glide after an idle delay. */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  if (document.documentElement.getAttribute('dir') === 'rtl') {
    return;
  }

  const selector = '.template-index :is(.shopify-section--featured-collections, .shopify-section--cyme-classics-edit) scroll-carousel.product-list--carousel';
  const speedPxPerSec = 27;
  const startDelayMs = 900;
  const resumeDelayMs = 3600;
  const canHover = window.matchMedia('(hover: hover)').matches;

  // Arm first, build on first visibility: cloning and wrap math need real
  // layout, which is only guaranteed once the rail has actually intersected
  // the viewport (a script running at DOMContentLoaded can see 0-width cells
  // on slow devices and mis-size the whole marquee).
  const armRail = (carousel) => {
    if (carousel.dataset.cymeMarquee) {
      return;
    }

    carousel.dataset.cymeMarquee = 'armed';
    carousel.dataset.cymeMarqueeState = 'armed';

    if ('IntersectionObserver' in window) {
      const armObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          armObserver.disconnect();
          buildRail(carousel);
        }
      }, { threshold: 0.1 });

      armObserver.observe(carousel);
    } else {
      window.addEventListener('load', () => buildRail(carousel), { once: true });
    }
  };

  const buildRail = (carousel) => {
    if (carousel.dataset.cymeMarquee === 'true' || !carousel.isConnected) {
      return;
    }

    const cells = Array.from(carousel.children);

    if (cells.length < 2 || carousel.scrollWidth <= carousel.clientWidth + 40) {
      carousel.dataset.cymeMarqueeState = 'skipped-narrow';
      return;
    }

    if (cells[0].getBoundingClientRect().width < 40) {
      carousel.dataset.cymeMarqueeState = 'skipped-unlaid-out';
      return;
    }

    carousel.dataset.cymeMarquee = 'true';
    carousel.classList.add('cyme-marquee');

    const createClone = (cell) => {
      const clone = cell.cloneNode(true);
      clone.setAttribute('data-cyme-rail-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('reveal-on-scroll');
      clone.querySelectorAll('[reveal-on-scroll]').forEach((node) => node.removeAttribute('reveal-on-scroll'));
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea').forEach((node) => node.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('button, input, select, textarea').forEach((node) => {
        node.setAttribute('aria-disabled', 'true');
        node.style.pointerEvents = 'none';
      });

      return clone;
    };

    // Seamless wrap: mirror leading cells at the end until they cover one
    // viewport, so the jump back by one period lands on identical pixels.
    // A rail can initialize on a narrow phone and later grow after rotation;
    // extend the buffer on demand so that transition never exposes a blank tail.
    const ensureCloneCoverage = () => {
      const clones = Array.from(carousel.querySelectorAll('[data-cyme-rail-clone]'));

      const coverage = () => {
        const firstClone = clones[0];
        const lastClone = clones[clones.length - 1];
        return firstClone && lastClone
          ? lastClone.offsetLeft + lastClone.offsetWidth - firstClone.offsetLeft
          : 0;
      };

      while (coverage() < carousel.clientWidth + 80) {
        const sourceCell = cells[clones.length % cells.length];

        if (sourceCell.getBoundingClientRect().width < 1) {
          break;
        }

        const clone = createClone(sourceCell);
        carousel.append(clone);
        clones.push(clone);
      }
    };

    ensureCloneCoverage();

    let wrapAt = 0;

    const computeWrap = () => {
      const firstClone = carousel.querySelector('[data-cyme-rail-clone]');
      wrapAt = firstClone ? firstClone.offsetLeft - cells[0].offsetLeft : 0;
    };

    computeWrap();

    let pos = carousel.scrollLeft;
    let rafId = 0;
    let lastTime = 0;
    let hoverPause = false;
    let focusPause = false;
    let holdPause = false;
    // buildRail only runs once the rail has intersected the viewport.
    let inView = true;
    let resumeAt = performance.now() + startDelayMs;
    let resumeTimer = 0;

    const shouldRun = () =>
      inView &&
      !hoverPause &&
      !focusPause &&
      !holdPause &&
      wrapAt > 0 &&
      document.visibilityState === 'visible' &&
      performance.now() >= resumeAt;

    // Field-observable state for QA: why the rail is (not) gliding right now.
    const stampState = () => {
      carousel.dataset.cymeMarqueeState =
        !inView ? 'offscreen'
        : hoverPause ? 'paused-hover'
        : focusPause ? 'paused-focus'
        : holdPause ? 'paused-drag'
        : document.visibilityState !== 'visible' ? 'paused-hidden'
        : performance.now() < resumeAt ? 'waiting'
        : wrapAt <= 0 ? 'no-wrap'
        : 'running';
    };

    const stop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }

      lastTime = 0;
    };

    const frame = (time) => {
      rafId = 0;

      if (!carousel.isConnected) {
        stop();
        return;
      }

      if (!shouldRun()) {
        stop();
        return;
      }

      if (lastTime) {
        const dt = Math.min((time - lastTime) / 1000, 0.1);
        pos += speedPxPerSec * dt;

        if (pos >= wrapAt) {
          pos -= wrapAt;
        }

        // The rail computes scroll-behavior smooth, so a bare scrollLeft
        // assignment becomes an async smooth animation that the next frame
        // interrupts - the rail never actually moves (the original marquee
        // bug). scrollTo({behavior:'instant'}) fixed that but THROWS a
        // TypeError per frame on Chromium 89-114 / Samsung Internet 15-22
        // ('instant' joined the ScrollBehavior enum in ~M115), freezing the
        // rail and spamming Clarity with errors (#355). An inline style
        // override beats the CSS per spec on every engine, no enum involved;
        // the arrows keep their own smooth calls.
        var prevBehavior = carousel.style.scrollBehavior;
        carousel.style.scrollBehavior = 'auto';
        carousel.scrollLeft = pos;
        carousel.style.scrollBehavior = prevBehavior;
      }

      lastTime = time;
      rafId = requestAnimationFrame(frame);
    };

    const sync = () => {
      // A wrap period shorter than the viewport means layout moved under us
      // (fonts, images, breakpoint) - re-measure before trusting it.
      if (wrapAt < carousel.clientWidth) {
        computeWrap();
      }

      if (shouldRun()) {
        if (!rafId) {
          // Re-enter wherever the user left the rail, normalized into one period.
          pos = wrapAt > 0 ? carousel.scrollLeft % wrapAt : carousel.scrollLeft;
          lastTime = 0;
          rafId = requestAnimationFrame(frame);
        }
      } else {
        stop();
      }

      stampState();
    };

    // Real interaction: stop gliding, let the native carousel take over,
    // come back after an idle delay.
    const handOver = () => {
      resumeAt = performance.now() + resumeDelayMs;
      stop();
      stampState();
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(sync, resumeDelayMs + 60);
    };

    if (canHover) {
      carousel.addEventListener('mouseenter', () => {
        hoverPause = true;
        sync();
      });
      carousel.addEventListener('mouseleave', () => {
        hoverPause = false;
        sync();
      });
    }

    carousel.addEventListener('focusin', () => {
      focusPause = true;
      sync();
    });
    carousel.addEventListener('focusout', () => {
      focusPause = false;
      sync();
    });

    carousel.addEventListener('pointerdown', () => {
      holdPause = true;
      stop();
      stampState();
    }, { passive: true });
    window.addEventListener('pointerup', () => {
      if (holdPause) {
        holdPause = false;
        handOver();
      }
    }, { passive: true });
    window.addEventListener('pointercancel', () => {
      if (holdPause) {
        holdPause = false;
        handOver();
      }
    }, { passive: true });
    carousel.addEventListener('wheel', (event) => {
      // Only horizontal intent grabs the rail; vertical wheel is the user
      // scrolling the PAGE past the rail and must not pause the glide
      // (this was why the marquee read as broken while browsing).
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        handOver();
      }
    }, { passive: true });

    // Arrow buttons live outside the scroll container, inside the same rail.
    const controls = carousel.closest('product-list') || carousel.parentElement;

    if (controls) {
      controls.querySelectorAll('carousel-prev-button, carousel-next-button').forEach((button) => {
        button.addEventListener('pointerdown', handOver, { passive: true });
      });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        sync();
      }, { threshold: 0.05 });

      observer.observe(carousel);
    }

    window.clearTimeout(resumeTimer);
    resumeTimer = window.setTimeout(sync, startDelayMs + 60);

    document.addEventListener('visibilitychange', sync);

    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        ensureCloneCoverage();
        computeWrap();
        sync();
      }, 180);
    });
  };

  const init = () => {
    document.querySelectorAll(selector).forEach(armRail);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
/* CYME CUSTOM END: homepage product rail marquee */
